import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithReauth from './base';
import {
  CreateWireRequestDTO, UpdateWireRequestDTO, WireRequestDTO, CreateWireTransferDTO, WireTransferDTO, ApproveWireTransferDTO, CreateApprovedWireRequestDTO,
  WireTransferReportItemDTO, UpdateWireTransferDTO,
} from '../../models/wire';
import { Response } from '../../models/shared';
import { eventsApi } from './events';
import { toArgentinaReportBounds } from '../../utils/dates';

const wireApi = createApi({
  reducerPath: 'wireApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['wire'],
  endpoints: (builder) => ({
    getWiresRequests: builder.query<WireRequestDTO[], { eventId?: string; businessId?: string, pending?: boolean }>({
      query: ({ eventId, businessId, pending }) => ({
        url: (eventId && businessId) ? `/businesses/${businessId}/events/${eventId}/wire-requests` : '/wire-requests',
        method: 'GET',
        params: {
          pending,
        },
      }),
      providesTags: ['wire'],
      transformResponse: (response: Response<WireRequestDTO[]>) => response.data || [],
    }),
    createWireRequest: builder.mutation<WireRequestDTO, { eventId: string; businessId?: string; wireData: CreateWireRequestDTO }>({
      query: ({ eventId, businessId, wireData }) => ({
        url: (businessId && eventId) ? `/businesses/${businessId}/events/${eventId}/wire-requests` : `/events/${eventId}/wire-requests`,
        method: 'POST',
        body: wireData,
      }),
      invalidatesTags: ['wire'],
    }),
    createApprovedWireRequest: builder.mutation<WireRequestDTO, { eventId: string; businessId?: string; wireData: CreateApprovedWireRequestDTO }>({
      query: ({ eventId, businessId, wireData }) => ({
        url: `/businesses/${businessId}/events/${eventId}/wire-requests/approved`,
        method: 'POST',
        body: wireData,
      }),
      invalidatesTags: ['wire'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(eventsApi.util.invalidateTags(['events']));
        } catch (e) {
          console.error(e);
        }
      },
    }),
    updateWireRequest: builder.mutation<WireRequestDTO, { eventId: string; businessId?: string; wireData: UpdateWireRequestDTO }>({
      query: ({ eventId, businessId, wireData }) => ({
        url: (businessId && eventId) ? `/businesses/${businessId}/events/${eventId}/wire-requests` : `/events/${eventId}/wire-requests`,
        method: 'PUT',
        body: wireData,
      }),
      invalidatesTags: ['wire'],
    }),
    deleteWireRequest: builder.mutation<WireRequestDTO, { eventId: string; businessId?: string; wireId: string }>({
      query: ({ eventId, businessId, wireId }) => ({
        url: (businessId && eventId) ? `/businesses/${businessId}/events/${eventId}/wire-requests/${wireId}` : `/events/${eventId}/wire-requests/${wireId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['wire'],
    }),
    createTransfer: builder.mutation<WireTransferDTO, { eventId: string; wireData: CreateWireTransferDTO }>({
      query: ({ eventId, wireData }) => ({
        url: `/events/${eventId}/wires`,
        method: 'POST',
        body: wireData,
      }),
      invalidatesTags: ['wire'],
    }),
    updateTransfer: builder.mutation<WireTransferDTO, { eventId: string; wireData: UpdateWireTransferDTO }>({
      query: ({ eventId, wireData }) => ({
        url: `/events/${eventId}/wires`,
        method: 'PUT',
        body: wireData,
      }),
      invalidatesTags: ['wire'],
    }),
    approveRequest: builder.mutation<WireTransferDTO, { eventId: string; wireId: string; wireData: ApproveWireTransferDTO }>({
      query: ({ eventId, wireId, wireData }) => ({
        url: `/events/${eventId}/wire-requests/${wireId}/approve`,
        method: 'PATCH',
        body: {
          pdf_url: wireData.receipt_pdf_url,
          description: wireData.description,
        },
      }),
      invalidatesTags: ['wire'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(eventsApi.util.invalidateTags(['events']));
        } catch (e) {
          console.error(e);
        }
      },
    }),
    downloadWires: builder.mutation<void, { eventId: string; businessId: string }>({
      query: ({ eventId, businessId }) => ({
        url: `businesses/${businessId}/events/${eventId}/wire-requests/xlsx/download`,
        method: 'GET',
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `solicitudes-de-transferencia-${new Date().toLocaleDateString('es-AR', { year: 'numeric', month: '2-digit', day: '2-digit' })}-${eventId}.xlsx`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        },
      }),
    }),
    getWireTransfersReport: builder.query<WireTransferReportItemDTO[], { start: string; end: string }>({
      query: ({ start, end }) => {
        const bounds = toArgentinaReportBounds(start, end);
        return {
          url: '/wire-transfers',
          method: 'GET',
          params: { start: bounds.start, end: bounds.end },
        };
      },
      providesTags: ['wire'],
      transformResponse: (response: Response<WireTransferReportItemDTO[]>) => response.data || [],
    }),
    downloadWireTransfersReport: builder.mutation<void, { start: string; end: string }>({
      query: ({ start, end }) => {
        const bounds = toArgentinaReportBounds(start, end);
        return {
          url: '/wire-transfers/xlsx/download',
          method: 'GET',
          params: { start: bounds.start, end: bounds.end },
          responseHandler: async (response) => {
            // sin este guard un 400/403 se descargaría como si fuera el xlsx
            if (!response.ok) {
              return response.json();
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `transferencias-a-productoras-${start}-${end}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            return null;
          },
        };
      },
    }),
    deleteTransfer: builder.mutation<WireTransferDTO, { eventId: string; wireId: string }>({
      query: ({ eventId, wireId }) => ({
        url: `/events/${eventId}/wires/${wireId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['wire'],
    }),
  }),
});

export const {
  useGetWiresRequestsQuery,
  useCreateWireRequestMutation,
  useUpdateWireRequestMutation,
  useDeleteWireRequestMutation,
  useCreateTransferMutation,
  useUpdateTransferMutation,
  useApproveRequestMutation,
  useCreateApprovedWireRequestMutation,
  useDownloadWiresMutation,
  useDeleteTransferMutation,
  useGetWireTransfersReportQuery,
  useDownloadWireTransfersReportMutation,
} = wireApi;

export { wireApi };
